import { CacheHandler } from '@ember-data/store';
import { generateRequestId } from 'tio-common/utils/request-id';
import { getOwner, setOwner } from '@ember/owner';
import { LegacyNetworkHandler } from '@ember-data/legacy-compat';
import { service } from '@ember/service';
import { timeZone } from 'tio-common/utils/date/time-zone';
import ENV from 'tio-employee/config/environment';
import Fetch from '@ember-data/request/fetch';
import RequestManager from '@ember-data/request';
import SessionService from 'tio-employee/services/session';
import Store from 'ember-data/store';
import type { RequestContext, NextFn } from '@ember-data/request';

const RENEW_THRESHOLD_MS = 1800000; // 30 minutes before expiration
const RENEWAL_COOLDOWN_MS = 5000; // Prevent duplicate renewals within 5 seconds

class AuthHandler {
  @service declare session: SessionService;

  private nextRenewalAllowedAt = 0;

  async request<T>(context: RequestContext, next: NextFn<T>) {
    const { request } = context;
    const headers = new Headers(request.headers);

    // Add API headers
    headers.append('Content-Type', 'application/json');
    headers.append('x-api-key', ENV.apiKey);
    headers.append('Time-Zone', timeZone());
    headers.append('X-Request-Id', generateRequestId());

    // Add auth token if logged in
    if (this.session?.isAuthenticated && this.session.data?.authenticated?.access_token) {
      headers.set('tio-auth-token', this.session.data.authenticated.access_token);

      // Check if session needs renewal (fire-and-forget, don't block the request)
      this.maybeRenewSession();
    }

    return next({ ...request, headers });
  }

  /**
   * Renews the session token if it's within 30 minutes of expiring.
   * Runs in background without blocking the current request.
   */
  private maybeRenewSession(): void {
    const now = Date.now();
    if (now < this.nextRenewalAllowedAt) return;

    const expiresAt = this.session.expireAt;
    if (!expiresAt) return;

    const timeLeft = expiresAt - now;

    if (timeLeft < RENEW_THRESHOLD_MS && timeLeft > 0) {
      this.nextRenewalAllowedAt = now + RENEWAL_COOLDOWN_MS;

      // Fire-and-forget async block
      (async () => {
        try {
          if (!this.session?.isAuthenticated) {
            return;
          }
          const currentToken = this.session.data?.authenticated?.access_token;
          if (!currentToken) {
            return;
          }
          await this.session.renew();
        } catch (error) {
          // Back off longer on failure to avoid hammering a broken endpoint
          this.nextRenewalAllowedAt = Date.now() + 60000;
          console.error('Failed to renew session:', error);
        }
      })();
    }
  }
}

export default class AppStore extends Store {
  // @ts-expect-error: need to do more research on how to type this properly
  constructor(...args) {
    super(...args);

    const owner = getOwner(this);
    const authHandler = new AuthHandler();
    // @ts-expect-error: need to do more research on how to type this properly
    setOwner(authHandler, owner);
    this.requestManager = new RequestManager()
      .use([LegacyNetworkHandler, authHandler, Fetch])
      .useCache(CacheHandler);
  }
}
